import React, { useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    Tooltip,
    Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    ContentCopy
} from '@mui/icons-material';
import {
    formatTimestamp,
    formatPrice,
    formatSlippage,
    formatLatency,
    getSideDisplay,
    getPerformanceBadge,
    calculateQualityScore,
    isNewExecution,
    getIntentLabel
} from './utils';

/**
 * MetricsTable Component
 * Professional table-based display for execution metrics
 * Optimized for high-density data display and quick scanning
 */
const MetricsTable = ({ executions }) => {
    const theme = useTheme();
    const [expandedRow, setExpandedRow] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const handleToggleRow = (clOrdId) => {
        setExpandedRow(expandedRow === clOrdId ? null : clOrdId);
    };

    const handleCopyOrderId = (e, orderId) => {
        e.stopPropagation();
        navigator.clipboard.writeText(orderId || '');
        setCopiedId(orderId);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Table cell styling for compact, professional look
    const cellStyle = {
        py: 0.5,
        px: 1.25,
        fontSize: '0.875rem',
        borderBottom: `1px solid ${theme.palette.divider}`,
        whiteSpace: 'nowrap'
    };

    const headerCellStyle = {
        ...cellStyle,
        fontWeight: 700,
        backgroundColor: 'rgba(30, 32, 36, 1)',
        color: theme.palette.text.primary,
        textTransform: 'uppercase',
        fontSize: '0.8125rem',
        letterSpacing: '0.75px',
        py: 0.75
    };

    // Render expanded detail row
    const renderDetailRow = (execution) => {
        const {
            clOrdId,
            orderId,
            execId,
            ordStatus,
            bidAtSubmit,
            askAtSubmit,
            midPriceAtSubmit,
            spreadAtSubmit,
            avgPx,
            totalLatencyMs,
            clockSkewMs,
            strategyId,
            legNumber,
            intent,
            accountNumber
        } = execution;

        return (
            <TableRow>
                <TableCell
                    colSpan={11}
                    sx={{
                        py: 0,
                        px: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: 'transparent'
                    }}
                >
                    <Collapse in={expandedRow === (clOrdId || orderId)} timeout={200}>
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                backgroundColor: 'rgba(41, 43, 47, 1)',
                                borderTop: `1px solid ${theme.palette.divider}`,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            {/* Three column grid for details */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(3, 1fr)'
                                    },
                                    gap: 2,
                                    fontSize: '0.875rem'
                                }}
                            >
                                {/* Column 1: Order Details */}
                                <Box>
                                    <Box sx={{ mb: 1, pb: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                        <Box
                                            sx={{
                                                fontWeight: 700,
                                                color: theme.palette.text.primary,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Order Details
                                        </Box>
                                    </Box>
                                    <DetailRow label="Order ID" value={orderId} mono />
                                    <DetailRow label="Client Order ID" value={clOrdId || '--'} mono />
                                    <DetailRow label="Execution ID" value={execId || '--'} mono />
                                    <DetailRow
                                        label="Status"
                                        value={ordStatus === '2' ? '✅ Filled' : `Status: ${ordStatus}`}
                                    />
                                    <DetailRow label="Account" value={accountNumber || '--'} mono />
                                    {strategyId !== 0 && strategyId !== null && (
                                        <>
                                            <Box sx={{ mt: 1, mb: 0.5, borderTop: `1px solid ${theme.palette.divider}`, pt: 0.75 }} />
                                            <DetailRow label="Strategy ID" value={strategyId} mono />
                                            <DetailRow label="Leg Number" value={legNumber || '--'} />
                                            <DetailRow label="Intent" value={getIntentLabel(intent)} />
                                        </>
                                    )}
                                </Box>

                                {/* Column 2: Market Data */}
                                <Box>
                                    <Box sx={{ mb: 1, pb: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                        <Box
                                            sx={{
                                                fontWeight: 700,
                                                color: theme.palette.text.primary,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Market Data
                                        </Box>
                                    </Box>
                                    <Box sx={{ fontSize: '0.8125rem', color: 'rgba(120, 180, 220, 1)', mb: 0.5, fontWeight: 600 }}>
                                        AT SUBMISSION:
                                    </Box>
                                    <DetailRow label="Bid" value={formatPrice(bidAtSubmit)} mono />
                                    <DetailRow label="Ask" value={formatPrice(askAtSubmit)} mono />
                                    <DetailRow label="Mid" value={formatPrice(midPriceAtSubmit)} mono />
                                    <DetailRow label="Spread" value={`${formatPrice(spreadAtSubmit, 2)} pips`} mono />
                                    <Box sx={{ my: 0.75, borderTop: `1px solid ${theme.palette.divider}` }} />
                                    <Box sx={{ fontSize: '0.8125rem', color: 'rgba(120, 180, 220, 1)', mb: 0.5, fontWeight: 600 }}>
                                        AT EXECUTION:
                                    </Box>
                                    <DetailRow label="Fill Price" value={formatPrice(avgPx)} mono highlight />
                                </Box>

                                {/* Column 3: Performance Breakdown */}
                                <Box>
                                    <Box sx={{ mb: 1, pb: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                        <Box
                                            sx={{
                                                fontWeight: 700,
                                                color: theme.palette.text.primary,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Performance Breakdown
                                        </Box>
                                    </Box>
                                    <DetailRow label="Total Latency" value={formatLatency(totalLatencyMs)} mono />
                                    <DetailRow label="Clock Skew" value={formatLatency(clockSkewMs)} mono />
                                    <Box sx={{ my: 0.75, borderTop: `1px solid ${theme.palette.divider}` }} />
                                    <Box sx={{ fontSize: '0.8125rem', color: 'rgba(120, 180, 220, 1)', mb: 0.5, fontWeight: 600 }}>
                                        LATENCY COMPONENTS:
                                    </Box>
                                    <DetailRow
                                        label="Broker Processing"
                                        value={formatLatency(execution.submissionToFillLatencyMs)}
                                        mono
                                    />
                                    <DetailRow
                                        label="Network + Internal"
                                        value={formatLatency(totalLatencyMs - execution.submissionToFillLatencyMs)}
                                        mono
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        );
    };

    // Helper for detail rows in expanded view
    const DetailRow = ({ label, value, mono = false, highlight = false }) => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.25,
                fontSize: '0.875rem',
                alignItems: 'center'
            }}
        >
            <Box
                sx={{
                    color: 'rgba(140, 200, 240, 1)',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                }}
            >
                {label}:
            </Box>
            <Box
                sx={{
                    fontFamily: mono ? 'monospace' : 'inherit',
                    fontWeight: highlight ? 700 : 600,
                    color: highlight ? 'rgba(100, 200, 255, 1)' : theme.palette.text.primary,
                    fontSize: '0.875rem'
                }}
            >
                {value}
            </Box>
        </Box>
    );

    return (
        <TableContainer
            sx={{
                flex: 1,
                overflow: 'auto',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
            }}
        >
            <Table
                stickyHeader
                size="small"
                sx={{
                    '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...headerCellStyle, width: 40 }}></TableCell>
                        <TableCell sx={headerCellStyle}>Time</TableCell>
                        <TableCell sx={headerCellStyle}>Symbol</TableCell>
                        <TableCell sx={headerCellStyle}>Side</TableCell>
                        <TableCell sx={{ ...headerCellStyle, textAlign: 'right' }}>Qty</TableCell>
                        <TableCell sx={{ ...headerCellStyle, textAlign: 'right' }}>Price</TableCell>
                        <TableCell sx={{ ...headerCellStyle, textAlign: 'right' }}>Latency</TableCell>
                        <TableCell sx={{ ...headerCellStyle, textAlign: 'right' }}>Slippage</TableCell>
                        <TableCell sx={{ ...headerCellStyle, textAlign: 'center' }}>Quality</TableCell>
                        <TableCell sx={headerCellStyle}>LP</TableCell>
                        <TableCell sx={{ ...headerCellStyle, width: 60 }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {executions.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={11} sx={{ textAlign: 'center', py: 8 }}>
                                <Box sx={{ color: 'rgba(140, 200, 240, 1)' }}>
                                    <Box sx={{ fontSize: '2rem', mb: 1, opacity: 0.4 }}>📊</Box>
                                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>No execution metrics yet</Box>
                                    <Box sx={{ fontSize: '0.8125rem' }}>
                                        Execution metrics will appear here in real-time
                                    </Box>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        executions.map((execution, index) => {
                            const {
                                time,
                                symbolName,
                                side,
                                orderQty,
                                avgPx,
                                submissionToFillLatencyMs,
                                slippagePips,
                                lpName,
                                orderId,
                                clOrdId
                            } = execution;

                            const isNew = isNewExecution(time);
                            const latencyBadge = getPerformanceBadge(submissionToFillLatencyMs, 'latency');
                            const slippageBadge = getPerformanceBadge(slippagePips, 'slippage');
                            const sideDisplay = getSideDisplay(side);
                            const qualityScore = calculateQualityScore(submissionToFillLatencyMs, slippagePips);
                            const isExpanded = expandedRow === (clOrdId || orderId);
                            const rowKey = clOrdId || orderId || index;

                            return (
                                <React.Fragment key={rowKey}>
                                    <TableRow
                                        hover
                                        onClick={() => handleToggleRow(clOrdId || orderId)}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: isNew
                                                ? `${theme.palette.primary.main}08`
                                                : index % 2 === 0
                                                    ? 'rgba(35, 37, 41, 1)'
                                                    : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(50, 52, 56, 1)'
                                            },
                                            borderLeft: isNew ? `3px solid ${theme.palette.primary.main}` : 'none'
                                        }}
                                    >
                                        <TableCell sx={cellStyle}>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    padding: 0.5,
                                                    color: 'rgba(140, 200, 240, 1)',
                                                    transition: 'transform 0.2s',
                                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(140, 200, 240, 0.1)'
                                                    }
                                                }}
                                            >
                                                <KeyboardArrowDown fontSize="small" />
                                            </IconButton>
                                        </TableCell>

                                        <TableCell sx={{ ...cellStyle, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                                            {isNew && (
                                                <Chip
                                                    label="NEW"
                                                    size="small"
                                                    sx={{
                                                        height: 16,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: '#fff',
                                                        mr: 0.5,
                                                        '& .MuiChip-label': { px: 0.6 }
                                                    }}
                                                />
                                            )}
                                            {formatTimestamp(time)}
                                        </TableCell>

                                        <TableCell sx={{ ...cellStyle, fontWeight: 700, color: 'rgba(100, 200, 255, 1)' }}>
                                            {symbolName}
                                        </TableCell>

                                        <TableCell sx={cellStyle}>
                                            <Chip
                                                label={sideDisplay.label}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    backgroundColor: sideDisplay.bgColor,
                                                    color: sideDisplay.color,
                                                    '& .MuiChip-label': { px: 0.75 }
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                ...cellStyle,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                fontWeight: 600
                                            }}
                                        >
                                            {orderQty}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                ...cellStyle,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                fontWeight: 600
                                            }}
                                        >
                                            {formatPrice(avgPx)}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                ...cellStyle,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                color: latencyBadge.color
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: latencyBadge.color
                                                    }}
                                                />
                                                {formatLatency(submissionToFillLatencyMs)}
                                            </Box>
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                ...cellStyle,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                color: slippageBadge.color
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: slippageBadge.color
                                                    }}
                                                />
                                                {formatSlippage(slippagePips)}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ ...cellStyle, textAlign: 'center' }}>
                                            <Chip
                                                label={qualityScore.score}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 700,
                                                    fontFamily: 'monospace',
                                                    backgroundColor: `${qualityScore.color}15`,
                                                    color: qualityScore.color,
                                                    border: `1px solid ${qualityScore.color}60`,
                                                    '& .MuiChip-label': { px: 0.75 }
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell sx={{ ...cellStyle, fontSize: '0.875rem' }}>
                                            {lpName}
                                        </TableCell>

                                        <TableCell sx={{ ...cellStyle, textAlign: 'center' }}>
                                            <Tooltip
                                                title={copiedId === orderId ? 'Copied!' : 'Copy Order ID'}
                                                arrow
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleCopyOrderId(e, orderId)}
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <ContentCopy sx={{ fontSize: '1.1rem', color: 'rgba(140, 200, 240, 1)' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    {renderDetailRow(execution)}
                                </React.Fragment>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default MetricsTable;
